import { Controller } from "@hotwired/stimulus";
import { createMap, loadMapLibre, retryFailedDownload, toLngLat } from "src/maps";

// The map is built each time the modal finishes opening, because MapLibre sizes
// itself to its container and a hidden modal has no size. It is removed again
// on close: list pages render one of these modals per row, and every live map
// holds a WebGL context, of which browsers allow only about 16.
export default class extends Controller {
  static targets = ["mapWrapper"]
  static values = { lat: String, lng: String }

  connect() {
    // Bootstrap 3 fires its modal events through jQuery only. The extra
    // namespace lets disconnect unbind these handlers and nothing else.
    $(this.element)
      .on("show.bs.modal.mapModal", () => this.prefetch())
      .on("shown.bs.modal.mapModal", () => this.open())
      .on("hidden.bs.modal.mapModal", () => this.close());
  }

  disconnect() {
    $(this.element).off(".mapModal");
    this.close();
  }

  // Starts the download while the modal animates open. Opening a modal is the
  // user asking for a map anew, so a download that failed before is tried
  // again. A failure now is left for createMap, which shows the fallback.
  prefetch() {
    retryFailedDownload();
    loadMapLibre().catch(() => {});
  }

  async open() {
    const opening = (this.opening = Symbol("opening"));
    const coordinates = { lat: this.latValue, lng: this.lngValue };
    const map = await createMap(this.mapWrapperTarget, { center: coordinates, zoom: 9 });
    if (!map) return;

    // Closed, or closed and reopened, while the style was loading.
    if (this.opening !== opening) {
      map.remove();
      // createMap marked the wrapper loaded on its way out; a closed modal has
      // no map state. Left alone when a reopening has already claimed it.
      if (!this.opening) delete this.mapWrapperTarget.dataset.mapState;
      return;
    }

    const { Marker } = await loadMapLibre();
    new Marker().setLngLat(toLngLat(coordinates)).addTo(map);
    this.map = map;
  }

  close() {
    this.opening = null;
    this.map?.remove();
    this.map = null;
    // Also drops a fallback message, so a closed modal holds no map state.
    this.mapWrapperTarget.replaceChildren();
    delete this.mapWrapperTarget.dataset.mapState;
  }
}
